import React, { useEffect, useMemo, useState } from "react";
import { ANIMALS } from "../../../../data/origami/animals";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

const CATS = new Set(["Alpi", "Blacky", "Nala", "Chameau", "Nougat", "Caramel"]);
const DOGS = new Set(["Savane", "Balthazar", "Choco", "Baloo", "Simba", "Harissa"]);
const HAMSTERS = new Set(["Pixel"]);
const SPIDER = new Set(["Spira"]);

const ALL = ANIMALS;

function categoryFor(name) {
  if (CATS.has(name)) return "cats";
  if (DOGS.has(name)) return "dogs";
  if (HAMSTERS.has(name)) return "hamsters";
  if (SPIDER.has(name)) return "spider";
  return null;
}

export default function AnimalCategoriesPuzzle({ storageKey, onSuccess, onAttempt }) {
  const initialState = useMemo(() => {
    const raw = storageKey ? localStorage.getItem(storageKey) : null;
    const parsed = raw ? safeParse(raw) : null;

    if (parsed?.bins) {
      const allNames = new Set(ALL);
      const clean = (arr) => (Array.isArray(arr) ? arr.filter((n) => allNames.has(n)) : []);
      const bins = {
        pool: clean(parsed.bins.pool),
        cats: clean(parsed.bins.cats),
        dogs: clean(parsed.bins.dogs),
        hamsters: clean(parsed.bins.hamsters),
        spider: clean(parsed.bins.spider),
      };
      const seen = new Set();
      const dedupe = (arr) => arr.filter((n) => (seen.has(n) ? false : (seen.add(n), true)));
      bins.pool = dedupe(bins.pool);
      bins.cats = dedupe(bins.cats);
      bins.dogs = dedupe(bins.dogs);
      bins.hamsters = dedupe(bins.hamsters);
      bins.spider = dedupe(bins.spider);
      const placed = new Set([...bins.pool, ...bins.cats, ...bins.dogs, ...bins.hamsters, ...bins.spider]);
      const missing = ALL.filter((n) => !placed.has(n));
      bins.pool = [...bins.pool, ...missing];
      return { bins, solved: !!parsed.solved };
    }

    return { bins: { pool: shuffle(ALL), cats: [], dogs: [], hamsters: [], spider: [] }, solved: false };
  }, [storageKey]);

  const [bins, setBins] = useState(initialState.bins);
  const [solved, setSolved] = useState(initialState.solved);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify({ solved, bins }));
  }, [bins, solved, storageKey]);

  function startDrag(e, name) {
    if (solved) return;
    setFeedback("");
    try {
      e.dataTransfer.setData("application/x-animal", JSON.stringify({ name }));
      e.dataTransfer.setData("text/plain", name);
      e.dataTransfer.effectAllowed = "move";
    } catch { }
  }

  function moveTo(e, binKey) {
    if (solved) return;
    let payload = null;
    try { payload = JSON.parse(e.dataTransfer.getData("application/x-animal") || "null"); } catch { }
    const name = payload?.name || e.dataTransfer.getData("text/plain");
    if (!name) return;
    setBins((prev) => {
      const next = {
        pool: prev.pool.filter((n) => n !== name),
        cats: prev.cats.filter((n) => n !== name),
        dogs: prev.dogs.filter((n) => n !== name),
        hamsters: prev.hamsters.filter((n) => n !== name),
        spider: prev.spider.filter((n) => n !== name),
      };
      next[binKey] = [...next[binKey], name];
      return next;
    });
    setFeedback("");
  }

  function moveBackToPool(name) {
    if (solved) return;
    setBins((prev) => ({
      pool: [...prev.pool, name],
      cats: prev.cats.filter((n) => n !== name),
      dogs: prev.dogs.filter((n) => n !== name),
      hamsters: prev.hamsters.filter((n) => n !== name),
      spider: prev.spider.filter((n) => n !== name),
    }));
    setFeedback("");
  }

  function check() {
    setFeedback("");
    const totalPlaced = bins.cats.length + bins.dogs.length + bins.hamsters.length + bins.spider.length;
    if (totalPlaced !== ALL.length || bins.pool.length !== 0) {
      onAttempt?.({ ok: false });
      setFeedback("Il manque des noms à classer. (Tout doit être placé dans une catégorie.)");
      return;
    }
    const ok =
      bins.cats.every((n) => categoryFor(n) === "cats") &&
      bins.dogs.every((n) => categoryFor(n) === "dogs") &&
      bins.hamsters.every((n) => categoryFor(n) === "hamsters") &&
      bins.spider.every((n) => categoryFor(n) === "spider");
    onAttempt?.({ ok });
    if (!ok) { setFeedback("❌ Certaines annotations ne collent pas. Reprends les indices et re-trie quelques noms."); return; }
    setSolved(true);
    setFeedback("✅ Classification validée. Il y a donc 6 chats, 6 chiens, 1 araignée et 1 hamster.");
    onSuccess?.();
  }

  function reset() {
    setBins({ pool: shuffle(ALL), cats: [], dogs: [], hamsters: [], spider: [] });
    setFeedback("");
    setSolved(false);
  }

  const Tag = ({ name, draggable, onDragStart, onClick, subtle }) => (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      className={[
        "inline-flex items-center justify-center px-2.5 py-1 text-xs leading-none rounded-lg border whitespace-nowrap",
        subtle ? "bg-black/35 border-white/10 text-slate-100" : "bg-white/5 border-white/15 text-slate-100",
        solved ? "opacity-70 cursor-default" : draggable ? "cursor-grab active:cursor-grabbing hover:bg-white/10" : "cursor-pointer hover:bg-white/10",
        "transition",
      ].join(" ")}
      title={solved ? "" : onClick ? "Cliquer pour remettre dans 'Non classés'" : "Glisser-déposer"}
    >
      {name}
    </div>
  );

  const Bin = ({ id, title, items }) => (
    <div
      onDragOver={(e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch { } }}
      onDrop={(e) => { e.preventDefault(); moveTo(e, id); }}
      className="rounded-2xl border border-white/10 bg-black/30 p-3"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-slate-100">{title}</p>
        <p className="text-[10px] text-slate-500">déposer ici</p>
      </div>
      <div className="mt-2 min-h-[52px] flex flex-wrap gap-2">
        {items.length === 0 ? (
          <span className="text-[11px] text-slate-500">Dépose ici…</span>
        ) : (
          items.map((name) => (
            <Tag key={name} name={name} draggable={!solved}
              onDragStart={(e) => startDrag(e, name)}
              onClick={() => moveBackToPool(name)}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <p className="text-sm text-slate-100 font-semibold">Annexe — animaux déclarés (à reconstituer)</p>
        <p className="mt-1 text-xs text-slate-300">
          Vous savez que les employés ne possèdent que 4 types d'animaux :
          <span className="font-semibold"> araignée, chats, chiens, hamsters</span>.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
          Annotations RH (à suivre à la lettre)
        </p>
        <ul className="mt-2 text-xs text-slate-200 list-disc pl-5 space-y-1">
          <li><span className="font-semibold">1 -</span> Un seul animal n'a ni poils, ni cage, ni laisse.</li>
          <li><span className="font-semibold">2 -</span> Son nom évoque une spirale.</li>
          <li><span className="font-semibold">3 -</span> Un chat porte le nom d'un animal… qui n'est pas un chat.</li>
          <li><span className="font-semibold">4 -</span> Un seul animal est gardé dans une cage contenant souvent une roue.</li>
          {/* Indice 5 reformulé : Baloo (ours) et Simba (lion), personnages de films d'animation connus */}
          <li><span className="font-semibold">5 -</span> Deux chiens portent le nom d'un célèbre ours et d'un célèbre lion de dessin animé.</li>
          <li><span className="font-semibold">6 -</span> Deux chats ont des noms de gourmandises non chocolatées.</li>
          <li><span className="font-semibold">7 -</span> L'animal en cage a un nom évoquant l'informatique.</li>
          <li><span className="font-semibold">8 -</span> Un chat a un nom évoquant une couleur sombre.</li>
          <li><span className="font-semibold">9 -</span> Un chien porte un nom évoquant un aliment piquant.</li>
          {/* Indice 10 reformulé : Choco → chocolat → Pâques était trop indirect */}
          <li><span className="font-semibold">10 -</span> Un chien porte le nom d'une friandise au cacao.</li>
          <li><span className="font-semibold">11 -</span> Un chat porte le nom d'un personnage de fiction féminin.</li>
          <li><span className="font-semibold">12 -</span> Un chien porte un nom évoquant un territoire naturel.</li>
          <li><span className="font-semibold">13 -</span> Un chat porte un nom court, montagnard.</li>
          <li><span className="font-semibold">14 -</span> Un chien est membre des rois mages.</li>
        </ul>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-semibold text-slate-100">Non classés</p>
          <p className="text-[10px] text-slate-500">glisser-déposer</p>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 select-none">
          <div
            onDragOver={(e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = "move"; } catch { } }}
            onDrop={(e) => { e.preventDefault(); moveTo(e, "pool"); }}
            className="mt-2 flex flex-wrap gap-2 select-none"
          >
            {bins.pool.map((name) => (
              <Tag key={name} name={name} draggable={!solved}
                onDragStart={(e) => startDrag(e, name)} subtle
              />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Bin id="cats" title="Chats" items={bins.cats} />
        <Bin id="dogs" title="Chiens" items={bins.dogs} />
        <Bin id="hamsters" title="Hamsters" items={bins.hamsters} />
        <Bin id="spider" title="Araignée" items={bins.spider} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={check} disabled={solved}
          className={["rounded-xl px-4 py-2 text-xs font-semibold transition",
            solved ? "bg-emerald-700/40 text-emerald-100 cursor-default" : "bg-indigo-600 hover:bg-indigo-500",
          ].join(" ")}
        >
          Vérifier
        </button>
        <button type="button" onClick={reset}
          className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 text-xs font-semibold transition"
        >
          Réinitialiser
        </button>
        {feedback && <p className="text-xs text-amber-200">{feedback}</p>}
      </div>
    </div>
  );
}